import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import { ProjetsService } from 'app/services/projets/projets.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';
import { Location } from '@angular/common';
import Swal from 'sweetalert2';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { MatSelect } from '@angular/material/select';

@Component({
  selector: 'app-modifier-projet',
  templateUrl: './modifier-projet.component.html',
  styleUrls: ['./modifier-projet.component.scss']
})
export class ModifierProjetComponent implements OnInit {
  @ViewChild('agencySelect') agencySelect!: MatSelect;
  @ViewChild('agencySearchInput') agencySearchInput!: ElementRef;

  projectForm: FormGroup;
  isLoading = false;
  isSaving = false;
  isDeletingImage = false;
  idProjetAgency: string;
  
  selectedFiles: FileList;
  promoSelectedFile: File;
  uploadedImages: string[] = [];
  uploadedPromoImage: string = '';
  
  allAgencies: any[] = [];
  filteredAgencies: any[] = [];
  categories: string[] = [];
  categoryInput: string = '';
  
  statusOptions = [
    { value: 'DISPONIBLE', label: 'Disponible', color: '#4CAF50' },
    { value: 'EN COURS', label: 'En cours', color: '#2196F3' },
    { value: 'VENDU', label: 'Vendu', color: '#F44336' }
  ];

  uploadProgress: number = 0;
  isUploading: boolean = false;

  agencySearchTerm: string = '';
  isAgencyDropdownOpen: boolean = false;

  originalData: any = null;

  constructor(
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private router: Router,
    private projetService: ProjetsService,
    private agenciesService: AgenciesService,
    private awsService: AwscontrolService,
    private fb: FormBuilder
  ) {
    this.idProjetAgency = this.activatedRoute.snapshot.paramMap.get('id') || '';
    this.initForm();
  }

  initForm(): void {
    this.projectForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      description: ['', [Validators.required, Validators.minLength(10)]],
      adresse: ['', Validators.maxLength(200)],
      date: ['', Validators.required],
      agencyName: ['', Validators.required],
      status: ['DISPONIBLE', Validators.required],
      videoUrlProjet: ['', this.urlValidator],
      nomCommercial: ['', Validators.maxLength(50)],
      numCommercial: ['', Validators.pattern('^[0-9+\\s-]*$')],
      emailCommercial: ['', Validators.email],
      projetLink: ['', this.urlValidator],
      promoTitle: ['', Validators.maxLength(100)],
      promoImg: [''],
      categoryList: [[]]
    });
  }

  urlValidator(control: AbstractControl): { [key: string]: boolean } | null {
    if (!control.value || control.value === '') {
      return null;
    }
    try {
      new URL(control.value);
      return null;
    } catch {
      return { invalidUrl: true };
    }
  }

  ngOnInit(): void {
    this.loadAgencies();
    this.loadProject();
    this.checkAwsConnection();
  }

  checkAwsConnection(): void {
    this.awsService.checkHealth().subscribe({
      next: (response) => {
        console.log('✅ AWS S3 Connection Healthy:', response);
      },
      error: (err) => {
        console.warn('⚠️ AWS S3 Connection Warning:', err);
      }
    });
  }

  loadProject(): void {
    this.isLoading = true;
    this.projetService.getProjet(this.idProjetAgency).subscribe({
      next: (data: any) => {
        console.log('Project data loaded:', data);
        this.populateForm(data);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading project:', error);
        this.isLoading = false;
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Impossible de charger les données du projet'
        }).then(() => {
          this.location.back();
        });
      }
    });
  }

  populateForm(project: any): void {
    this.originalData = { ...project };

    if (project.photos) {
      this.uploadedImages = this.extractImageUrls(project.photos);
    }
    
    if (project.promoImg) {
      const promoUrl = this.extractImageUrl(project.promoImg);
      if (promoUrl) {
        this.uploadedPromoImage = promoUrl;
      }
    }

    if (project.categoryList && Array.isArray(project.categoryList)) {
      this.categories = [...project.categoryList];
    }

    this.projectForm.patchValue({
      name: project.name || '',
      description: project.description || '',
      adresse: project.adresse || '',
      date: project.date ? new Date(project.date) : new Date(),
      agencyName: project.agencyName || '',
      status: project.status || 'DISPONIBLE',
      videoUrlProjet: project.videoUrlProjet || '',
      nomCommercial: project.nomCommercial || '',
      numCommercial: project.numCommercial || '',
      emailCommercial: project.emailCommercial || '',
      projetLink: project.projetLink || '',
      promoTitle: project.promoTitle || '',
      promoImg: this.uploadedPromoImage || ''
    });

    this.projectForm.patchValue({ categoryList: this.categories });
  }

  loadAgencies(): void {
    this.isLoading = true;
    this.agenciesService.getList().subscribe({
      next: (data: any) => {
        let agencies = [];
        if (Array.isArray(data)) {
          agencies = data;
        } else if (data) {
          agencies = [data];
        }
        this.allAgencies = agencies
          .filter(agency => 
            agency && 
            agency.name && 
            typeof agency.name === 'string' &&
            agency.role === 'promoteur-immobilieres'
          )
          .map(agency => ({
            id: agency.id,
            name: agency.name.trim(),
            role: agency.role,
            initial: agency.name.trim().charAt(0).toUpperCase(),
            color: this.getRandomColor()
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        
        this.filteredAgencies = [...this.allAgencies];
        this.isLoading = false;
        
        if (this.allAgencies.length === 0) {
          Swal.fire({
            icon: 'info',
            title: 'Aucun promoteur disponible',
            text: 'Aucune agence avec le rôle "promoteur immobilier" n\'a été trouvée'
          });
        }
      },
      error: (error) => {
        console.error('Erreur lors du chargement des agences:', error);
        this.allAgencies = [];
        this.filteredAgencies = [];
        this.isLoading = false;
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Impossible de charger la liste des promoteurs'
        });
      }
    });
  }

  private getRandomColor(): string {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#FFD166', '#06D6A0', '#118AB2',
      '#EF476F', '#7B68EE', '#20B2AA', '#FFA07A', '#9370DB'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  onAgencySelectOpened(): void {
    this.isAgencyDropdownOpen = true;
    this.agencySearchTerm = '';
    this.filterAgencies();
    setTimeout(() => {
      if (this.agencySearchInput) {
        this.agencySearchInput.nativeElement.focus();
      }
    }, 100);
  }

  onAgencySelectClosed(): void {
    this.isAgencyDropdownOpen = false;
    this.agencySearchTerm = '';
    this.filterAgencies();
  }

  filterAgencies(): void {
    if (!this.agencySearchTerm || this.agencySearchTerm.trim() === '') {
      this.filteredAgencies = [...this.allAgencies];
    } else {
      const term = this.agencySearchTerm.toLowerCase().trim();
      this.filteredAgencies = this.allAgencies.filter(agency => 
        agency.name.toLowerCase().includes(term)
      );
    }
  }

  clearAgencySearch(): void {
    this.agencySearchTerm = '';
    this.filterAgencies();
    if (this.agencySearchInput) {
      this.agencySearchInput.nativeElement.focus();
    }
  }

  selectAgency(agency: any): void {
    this.projectForm.patchValue({ agencyName: agency.name });
    this.agencySelect.close();
  }

  private extractImageUrl(image: any): string {
    if (typeof image === 'string') {
      return image;
    } else if (image && typeof image === 'object') {
      return image.url || image.location || image.src || '';
    }
    return '';
  }

  private extractImageUrls(images: any[]): string[] {
    if (!images || !Array.isArray(images)) {
      return [];
    }
    return images
      .map(image => this.extractImageUrl(image))
      .filter(url => url && url.trim() !== '');
  }

  onProjectImagesSelected(event: any): void {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    this.selectedFiles = files;
    this.uploadProjectImages();
  }

  uploadProjectImages(): void {
    if (!this.selectedFiles) return;

    this.isUploading = true;
    const filesArray = Array.from(this.selectedFiles);
    
    const validFiles = filesArray.filter(file => 
      file.type.match(/image\/(jpeg|png|jpg|gif|webp)/) && 
      file.size <= 5 * 1024 * 1024
    );

    if (validFiles.length === 0) {
      this.isUploading = false;
      Swal.fire({
        icon: 'warning',
        title: 'Aucune image valide',
        text: 'Veuillez sélectionner des images valides (JPEG, PNG, JPG, GIF, WEBP) de moins de 5MB'
      });
      return;
    }

    this.awsService.uploadFiles(validFiles, 'project-images').subscribe({
      next: (response) => {
        this.isUploading = false;
        
        if (response.success && response.urls && response.urls.length > 0) {
          this.uploadedImages.push(...response.urls);
          
          Swal.fire({
            icon: 'success',
            title: 'Images ajoutées',
            text: `${response.urls.length} image(s) ont été ajoutées avec succès`,
            timer: 2000,
            showConfirmButton: false
          });
        } else {
          throw new Error(response.error || 'Upload failed');
        }
      },
      error: (err) => {
        this.isUploading = false;
        console.error('❌ Error uploading images:', err);
        
        Swal.fire({
          icon: 'error',
          title: 'Erreur de téléchargement',
          text: 'Impossible de télécharger les images vers AWS S3',
          confirmButtonColor: '#f44336'
        });
      }
    });
  }

  onPromoImageSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.match(/image\/(jpeg|png|jpg|gif|webp)/)) {
      Swal.fire({
        icon: 'warning',
        title: 'Format non supporté',
        text: 'Veuillez sélectionner une image valide (JPEG, PNG, JPG, GIF, WEBP)',
        confirmButtonColor: '#ff9800'
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({
        icon: 'warning',
        title: 'Fichier trop volumineux',
        text: 'La taille maximale autorisée est de 5MB',
        confirmButtonColor: '#ff9800'
      });
      return;
    }

    this.promoSelectedFile = file;
    this.uploadPromoImage();
  }

  uploadPromoImage(): void {
    if (!this.promoSelectedFile) return;

    this.isUploading = true;

    this.awsService.uploadFile(this.promoSelectedFile, 'promo-images').subscribe({
      next: (response) => {
        this.isUploading = false;
        
        if (response.success && response.urls && response.urls.length > 0) {
          const uploadedUrl = response.urls[0];
          this.uploadedPromoImage = uploadedUrl;
          this.projectForm.patchValue({ promoImg: uploadedUrl });
          
          Swal.fire({
            icon: 'success',
            title: 'Image téléchargée',
            text: 'L\'image promotionnelle a été téléchargée avec succès',
            timer: 2000,
            showConfirmButton: false
          });
        } else {
          throw new Error(response.error || 'Upload failed');
        }
      },
      error: (err) => {
        this.isUploading = false;
        console.error('❌ Error uploading promo image:', err);
        
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Impossible de télécharger l\'image promotionnelle vers AWS S3',
          confirmButtonColor: '#f44336'
        });
      }
    });
  }

  private deleteImageFromS3(imageUrl: string, index: number, type: 'project' | 'promo'): void {
    this.isDeletingImage = true;

    this.awsService.deleteFile(imageUrl).subscribe({
      next: (response) => {
        this.isDeletingImage = false;
        console.log('✅ Image deleted from S3:', response);

        if (type === 'project' && index >= 0) {
          this.uploadedImages.splice(index, 1);
        } else if (type === 'promo') {
          this.uploadedPromoImage = '';
          this.projectForm.patchValue({ promoImg: '' });
        }

        Swal.fire({
          icon: 'success',
          title: 'Image supprimée',
          text: 'L\'image a été supprimée de AWS S3',
          timer: 1500,
          showConfirmButton: false
        });
      },
      error: (err) => {
        this.isDeletingImage = false;
        console.error('❌ Error deleting image:', err);

        Swal.fire({
          icon: 'error',
          title: 'Échec de la suppression',
          text: 'Impossible de supprimer l\'image de AWS S3',
          confirmButtonColor: '#f44336'
        });
      }
    });
  }

  // Move image up
  moveImageUp(index: number): void {
    if (index === 0) return;
    const temp = this.uploadedImages[index];
    this.uploadedImages[index] = this.uploadedImages[index - 1];
    this.uploadedImages[index - 1] = temp;
  }

  // Move image down
  moveImageDown(index: number): void {
    if (index === this.uploadedImages.length - 1) return;
    const temp = this.uploadedImages[index];
    this.uploadedImages[index] = this.uploadedImages[index + 1];
    this.uploadedImages[index + 1] = temp;
  }

  // Set image as primary (move to first position)
  setPrimaryImage(index: number): void {
    if (index === 0) return;
    const image = this.uploadedImages[index];
    this.uploadedImages.splice(index, 1);
    this.uploadedImages.unshift(image);
    Swal.fire({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 1500,
      icon: 'success',
      title: 'Image principale définie'
    });
  }

  removeProjectImage(index: number): void {
    const imageUrl = this.uploadedImages[index];

    Swal.fire({
      title: 'Supprimer cette image ?',
      text: 'L\'image sera définitivement supprimée de AWS S3.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.deleteImageFromS3(imageUrl, index, 'project');
      }
    });
  }

  removePromoImage(): void {
    if (!this.uploadedPromoImage) return;

    Swal.fire({
      title: 'Supprimer l\'image promotionnelle ?',
      text: 'L\'image sera définitivement supprimée de AWS S3.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.deleteImageFromS3(this.uploadedPromoImage, -1, 'promo');
      }
    });
  }

  addCategory(): void {
    if (this.categoryInput && this.categoryInput.trim() !== '') {
      const category = this.categoryInput.trim();
      if (!this.categories.includes(category)) {
        this.categories.push(category);
        this.projectForm.patchValue({ categoryList: this.categories });
      }
      this.categoryInput = '';
    }
  }

  removeCategory(category: string): void {
    const index = this.categories.indexOf(category);
    if (index >= 0) {
      this.categories.splice(index, 1);
      this.projectForm.patchValue({ categoryList: this.categories });
    }
  }

  onSubmit(): void {
    if (this.projectForm.invalid) {
      this.markFormGroupTouched(this.projectForm);
      
      const invalidControl = document.querySelector('.ng-invalid');
      if (invalidControl) {
        invalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      
      Swal.fire({
        icon: 'error',
        title: 'Formulaire invalide',
        text: 'Veuillez corriger les erreurs dans le formulaire'
      });
      return;
    }

    if (this.uploadedImages.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Images requises',
        text: 'Veuillez ajouter au moins une image pour le projet'
      });
      return;
    }

    const formValue = this.projectForm.value;
    const updateData = {
      ...formValue,
      photos: this.uploadedImages,
      categoryList: this.categories,
      promoImg: this.uploadedPromoImage || formValue.promoImg
    };

    Object.keys(updateData).forEach(key => {
      if (updateData[key] === '' || updateData[key] === null || updateData[key] === undefined) {
        delete updateData[key];
      }
    });

    this.isSaving = true;
    this.projetService.updateProjet(this.idProjetAgency, updateData).subscribe({
      next: (response) => {
        this.isSaving = false;
        Swal.fire({
          icon: 'success',
          title: 'Projet mis à jour !',
          text: 'Les modifications ont été enregistrées avec succès',
          showConfirmButton: false,
          timer: 2000
        }).then(() => {
          this.location.back();
        });
      },
      error: (error) => {
        this.isSaving = false;
        console.error('Error updating project:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Une erreur est survenue lors de la mise à jour du projet'
        });
      }
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getFieldError(fieldName: string): string {
    const field = this.projectForm.get(fieldName);
    if (!field || !field.errors || !field.touched) return '';

    if (field.errors['required']) return 'Ce champ est requis';
    if (field.errors['minlength']) return `Minimum ${field.errors['minlength'].requiredLength} caractères`;
    if (field.errors['maxlength']) return `Maximum ${field.errors['maxlength'].requiredLength} caractères`;
    if (field.errors['email']) return 'Email invalide';
    if (field.errors['pattern']) return 'Format invalide';
    if (field.errors['invalidUrl']) return 'URL invalide';

    return '';
  }

  navigateBack(): void {
    this.location.back();
  }

  hasChanges(): boolean {
    if (!this.originalData) return true;
    
    const currentData = this.projectForm.value;
    currentData.photos = this.uploadedImages;
    currentData.categoryList = this.categories;
    currentData.promoImg = this.uploadedPromoImage;
    
    return JSON.stringify(currentData) !== JSON.stringify(this.originalData);
  }

  getStatusColor(status: string): string {
    const statusOption = this.statusOptions.find(opt => opt.value === status);
    return statusOption ? statusOption.color : '#757575';
  }
}